'use client';

import { createContext, useContext, type CSSProperties } from 'react';

/**
 * Scoped "glass" styling flag for the shared Table system. Default false, so
 * every existing table (inventory lists, other entity pages) is unchanged.
 * Only the property-page tables set it true, which restyles the container and
 * rows to a frosted-glass surface while keeping all table functionality.
 */
const TableGlassContext = createContext(false);

export const TableGlassProvider = TableGlassContext.Provider;
export const useTableGlass = (): boolean => useContext(TableGlassContext);

// Overrides use !important to beat Table.module.css and inline row colors
// (#181818 / #1d1d1d / #2a2a2a).
export const TABLE_GLASS_CONTAINER = '!rounded-none !border-transparent !bg-transparent !shadow-none';
export const TABLE_GLASS_ROW = '!border-white/[0.06] !bg-transparent hover:!bg-white/[0.03]';
// Header shares the body's transparent fill so the table reads as one
// continuous surface (no darker header band). Scoped glass tables all lock
// filters, so the table has no internal vertical scroll to leak through it.
export const TABLE_GLASS_HEADER = '!bg-transparent !border-white/[0.06]';

// The native <select> chevron is painted by the browser flush against the control's right
// edge; padding moves the text, never the arrow. These two draw our own chevron at the same
// 8px inset every other control uses. Apply both together.
export const SELECT_CHEVRON_CLASS = 'appearance-none bg-no-repeat pr-7';
export const SELECT_CHEVRON_STYLE: CSSProperties = {
  backgroundImage:
    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.45)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
  backgroundPosition: 'right 8px center',
  backgroundSize: '12px 12px',
};
